import { createSlice } from '@reduxjs/toolkit'
import { generateBaseGrid, generateGridVersionPayload } from 'honeygrid-types'

import { FeatureNames } from '../names'

import type { PayloadAction } from '@reduxjs/toolkit'
import type {
	BaseGrid,
	GridVersion,
	GridVersionPayload,
	MapTool,
	ReferenceFields,
} from 'honeygrid-types'
import type { RootState } from '../../initStore'

// Grid model used in client state – always has a currentVersion object.
interface GridClient extends BaseGrid {
	currentVersion: GridVersionPayload
}

// Track which filters are in preview mode (showing draft values)
interface PreviewState {
	targetingFilters: Record<string, boolean>
}

export interface GridState {
	grid: GridClient | null // root metadata + committed version (read-only)
	draftVersion: GridVersionPayload | null // working copy for edits
	selectedVersionId: GridVersion['uuid'] | null // tracks which version is selected in UI
	preview: PreviewState // tracks what's being previewed
}

const initialState: () => GridState = () => {
	const baseMeta = generateBaseGrid({ empty: true })
	const version = generateGridVersionPayload({ empty: true })
	const grid: GridClient = { ...baseMeta, currentVersion: version }
	return {
		grid,
		draftVersion: version,
		selectedVersionId: null,
		preview: {
			targetingFilters: {},
		},
	}
}

export const gridSlice = createSlice({
	name: FeatureNames.Grid,
	initialState,
	reducers: {
		resetGrid: (state) => {
			const meta = generateBaseGrid({ empty: true })
			const ver = generateGridVersionPayload({ empty: true })
			state.grid = { ...meta, currentVersion: ver }
			state.draftVersion = ver
			state.selectedVersionId = null
			state.preview.targetingFilters = {}
		},
		setGrid: (state, action: PayloadAction<BaseGrid & { currentVersion?: GridVersionPayload }>) => {
			const ver = action.payload.currentVersion ?? generateGridVersionPayload({ empty: true })
			state.grid = { ...(action.payload as BaseGrid), currentVersion: ver }
			// Initialize draft as a copy of the current version
			state.draftVersion = { ...ver }
			// Reset selected version when setting a new grid
			state.selectedVersionId = null
			state.preview.targetingFilters = {}
		},
		setSelectedVersion: (
			state,
			action: PayloadAction<{ versionId: GridVersion['uuid']; version: GridVersionPayload }>
		) => {
			state.selectedVersionId = action.payload.versionId
			// Reset draft to match the new version
			state.draftVersion = { ...action.payload.version }
			// Clear preview state
			state.preview.targetingFilters = {}
		},
		setTargetingRules: (state, action: PayloadAction<GridVersionPayload['targeting_filters']>) => {
			if (state.draftVersion) state.draftVersion.targeting_filters = action.payload
		},
		removeTargetingFilter: (state, action: PayloadAction<string>) => {
			if (!state.draftVersion || !state.grid) return
			const referenceField = action.payload as ReferenceFields

			// Remove from draft version only
			const { [referenceField]: removed, ...remaining } = state.draftVersion.targeting_filters
			state.draftVersion.targeting_filters = remaining

			// Clear preview state for this filter
			delete state.preview.targetingFilters[referenceField]
		},
		updateTargetingFilter: (state, action: PayloadAction<{ id: string; percentage: number }>) => {
			if (!state.draftVersion) return
			const referenceField = action.payload.id as ReferenceFields
			state.draftVersion.targeting_filters[referenceField] = {
				percentage: action.payload.percentage,
			}
			// Mark this filter as being previewed
			state.preview.targetingFilters[referenceField] = true
		},
		setTargetingFilters: (
			state,
			action: PayloadAction<ReturnType<typeof generateGridVersionPayload>['targeting_filters']>
		) => {
			if (state.draftVersion) state.draftVersion.targeting_filters = action.payload
		},
		commitTargetingFilter: (state, action: PayloadAction<{ id: string; percentage: number }>) => {
			// This is called when user "applies" a filter change
			if (!state.draftVersion) return
			const referenceField = action.payload.id as ReferenceFields

			// Update the draft with the committed value
			if (!state.draftVersion.targeting_filters[referenceField]) {
				state.draftVersion.targeting_filters[referenceField] = { percentage: 0 }
			}
			state.draftVersion.targeting_filters[referenceField].percentage = action.payload.percentage

			// Remove from preview state since it's now committed
			delete state.preview.targetingFilters[referenceField]
		},
		setTargetingType: (state, action: PayloadAction<GridVersionPayload['targeting_type']>) => {
			if (!state.draftVersion) return
			state.draftVersion.targeting_type = action.payload
			return state
		},
		handleManualFilterChange: (
			state,
			action: PayloadAction<{ cellId: string; selectedTool: MapTool }>
		) => {
			if (!state.draftVersion) return
			if (!state.draftVersion.manual_inclusion_filters)
				state.draftVersion.manual_inclusion_filters = []
			const { cellId: id, selectedTool } = action.payload
			if (selectedTool === 'inclusion') {
				if (!state.draftVersion.manual_inclusion_filters)
					state.draftVersion.manual_inclusion_filters = []
				if (!state.draftVersion.manual_inclusion_filters.includes(id)) {
					// Add to inclusion and remove from exclusion
					state.draftVersion.manual_inclusion_filters.push(id)
					state.draftVersion.manual_exclusion_filters =
						state.draftVersion.manual_exclusion_filters?.filter((cell) => cell !== id) ?? []
				} else {
					// Remove from inclusion (toggle off)
					state.draftVersion.manual_inclusion_filters =
						state.draftVersion.manual_inclusion_filters.filter((cell) => cell !== id)
				}
			} else if (selectedTool === 'exclusion') {
				if (!state.draftVersion.manual_exclusion_filters)
					state.draftVersion.manual_exclusion_filters = []
				if (!state.draftVersion.manual_exclusion_filters.includes(id)) {
					// Add to exclusion and remove from inclusion
					state.draftVersion.manual_exclusion_filters.push(id)
					state.draftVersion.manual_inclusion_filters =
						state.draftVersion.manual_inclusion_filters?.filter((cell) => cell !== id)
				} else {
					// Remove from exclusion (toggle off)
					state.draftVersion.manual_exclusion_filters =
						state.draftVersion.manual_exclusion_filters.filter((cell) => cell !== id)
				}
			}
		},
		handleBulkManualFilterChange: (
			state,
			action: PayloadAction<{ cellIds: string[]; selectedTool: MapTool }>
		) => {
			if (!state.draftVersion) return
			if (!state.draftVersion.manual_inclusion_filters)
				state.draftVersion.manual_inclusion_filters = []
			const { cellIds: ids, selectedTool } = action.payload
			if (selectedTool === 'inclusion') {
				if (!state.draftVersion.manual_inclusion_filters)
					state.draftVersion.manual_inclusion_filters = []
				state.draftVersion.manual_inclusion_filters = [
					...new Set(state.draftVersion.manual_inclusion_filters.concat(ids)),
				]
				// Remove ids from exclusion filter if they exist
				if (state.draftVersion.manual_exclusion_filters) {
					state.draftVersion.manual_exclusion_filters =
						state.draftVersion.manual_exclusion_filters.filter((cell) => !ids.includes(cell))
				}
			} else if (selectedTool === 'exclusion') {
				if (!state.draftVersion.manual_exclusion_filters)
					state.draftVersion.manual_exclusion_filters = []
				state.draftVersion.manual_exclusion_filters = [
					...new Set(state.draftVersion.manual_exclusion_filters.concat(ids)),
				]
				// Remove ids from inclusion filter if they exist
				if (state.draftVersion.manual_inclusion_filters) {
					state.draftVersion.manual_inclusion_filters =
						state.draftVersion.manual_inclusion_filters.filter((cell) => !ids.includes(cell))
				}
			}
		},
		handleBulkManualFilterClear: (state, action: PayloadAction<{ cellIds: string[] }>) => {
			if (!state.draftVersion) return
			if (!state.draftVersion.manual_inclusion_filters)
				state.draftVersion.manual_inclusion_filters = []
			const { cellIds: ids } = action.payload
			if (state.draftVersion.manual_inclusion_filters) {
				state.draftVersion.manual_inclusion_filters =
					state.draftVersion.manual_inclusion_filters.filter((cell) => !ids.includes(cell))
			}
			if (state.draftVersion.manual_exclusion_filters) {
				state.draftVersion.manual_exclusion_filters =
					state.draftVersion.manual_exclusion_filters.filter((cell) => !ids.includes(cell))
			}
		},
		handleManualFilterReset: (state, action: PayloadAction<{ selectedTool: MapTool }>) => {
			if (!state.draftVersion) return
			if (!state.draftVersion.manual_inclusion_filters)
				state.draftVersion.manual_inclusion_filters = []
			const { selectedTool } = action.payload
			if (selectedTool === 'inclusion') {
				state.draftVersion.manual_inclusion_filters = []
			} else if (selectedTool === 'exclusion') {
				state.draftVersion.manual_exclusion_filters = []
			}
		},
		setTargetingMetrics: (
			state,
			action: PayloadAction<{
				households: number
				coverage: number
				targetedHouseholds: number
			}>
		) => {
			if (!state.draftVersion) return
			const { households, coverage, targetedHouseholds } = action.payload
			state.draftVersion.households = households
			state.draftVersion.targeting_coverage = coverage
			state.draftVersion.targeted_households = targetedHouseholds
		},
		setTargetedAreas: (state, action: PayloadAction<GridVersionPayload['targeted_areas']>) => {
			if (!state.draftVersion) return

			// Only update draftVersion - currentVersion should remain read-only
			state.draftVersion.targeted_areas = action.payload
		},
	},
})

export const {
	resetGrid,
	setGrid,
	setSelectedVersion,
	setTargetingRules,
	removeTargetingFilter,
	updateTargetingFilter,
	setTargetingFilters,
	commitTargetingFilter,
	setTargetingType,
	handleManualFilterChange,
	handleBulkManualFilterChange,
	handleBulkManualFilterClear,
	handleManualFilterReset,
	setTargetingMetrics,
	setTargetedAreas,
} = gridSlice.actions

export type GridSlice = typeof gridSlice.actions

// Selectors
export const gridDataSelector = (state: RootState) => state.grid.grid
export const versionSelector = (state: RootState) => state.grid.draftVersion
export const targetingFiltersSelector = (state: RootState) =>
	state.grid.draftVersion?.targeting_filters
export const targetingTypeSelector = (state: RootState) => state.grid.draftVersion?.targeting_type
export const manualInclusionFiltersSelector = (state: RootState) =>
	state.grid.draftVersion?.manual_inclusion_filters
export const manualExclusionFiltersSelector = (state: RootState) =>
	state.grid.draftVersion?.manual_exclusion_filters

// Selectors for committed/saved state
export const committedTargetingFiltersSelector = (state: RootState) =>
	state.grid.grid?.currentVersion?.targeting_filters

export const selectedVersionIdSelector = (state: RootState) => state.grid.selectedVersionId

// New selector for preview state
export const previewTargetingFiltersSelector = (state: RootState) =>
	state.grid.preview.targetingFilters

// Composite selector to get the effective targeting filters
// Returns draft values for filters in preview, committed values for others
export const effectiveTargetingFiltersSelector = (state: RootState) => {
	const committed = state.grid.grid?.currentVersion?.targeting_filters || {}
	const draft = state.grid.draftVersion?.targeting_filters || {}
	const previewing = state.grid.preview.targetingFilters

	const result: typeof committed = {}

	// Get all keys from both committed and draft
	const allKeys = new Set([...Object.keys(committed), ...Object.keys(draft)])

	for (const key of allKeys) {
		const refField = key as ReferenceFields
		if (previewing[refField]) {
			// Use draft value if previewing
			result[refField] = draft[refField]
		} else {
			// Use committed value otherwise
			result[refField] = committed[refField]
		}
	}

	return result
}
